import { useCallback } from 'react';
import { apiHooks } from '../../../hooks/useRestAPIHooks';
import { useNotify } from '../../../hooks';
import { defineMessages, useIntl } from 'react-intl';
import { TemplatingItem } from '../models';
import { useInvalidateTemplatingItems } from './useInvalidateTemplatingItems';
import { useSelectedTemplatingItem } from './useSelectedTemplatingItem';
import { useTemplatingItemTypeDisplay } from './useTemplatingItemTypeDisplay';
import { useResetTemplatingItemQueries } from './useManageTemplatingItemQueries';
import { useFavoriteTemplatingTemplates } from './useFavoriteTemplatingTemplates';

const messages = defineMessages({
  templatingItemDeleted: {
    id: 'templatingItemDeleted',
    defaultMessage: '{typeName} deleted',
  },
  templatingItemDeletedError: {
    id: 'templatingItemDeletedError',
    defaultMessage: 'Failed to delete {typeName}',
  },
});

export const useDeleteTemplatingItem = () => {
  const { formatMessage } = useIntl();
  const {
    setSelectedTemplatingItemId,
    selectedTemplatingItemQuery: { data: selectedTemplatingItem },
  } = useSelectedTemplatingItem();

  const templatingItemTypeDisplay = useTemplatingItemTypeDisplay();

  const [notify] = useNotify();

  const templatingItemDeleteMutation = apiHooks.useAPIMutation(
    'DELETE /v1/ai-templates/:id',
  );

  const invalidateTemplatingItems = useInvalidateTemplatingItems();
  const resetTemplatingItemQueries = useResetTemplatingItemQueries();
  const { removeFavoriteTemplate } = useFavoriteTemplatingTemplates();

  const deleteTemplatingItem = useCallback(
    async (templatingItem: TemplatingItem) => {
      await templatingItemDeleteMutation.mutateAsync(
        {
          id: templatingItem.id,
        },
        {
          onSuccess: () => {
            notify(
              formatMessage(messages.templatingItemDeleted, {
                typeName: templatingItemTypeDisplay[templatingItem.type],
              }),
              'success',
            );
            resetTemplatingItemQueries(templatingItem);
            invalidateTemplatingItems(templatingItem);
            if (templatingItem.type === 'template') {
              removeFavoriteTemplate(templatingItem);
            }
            if (selectedTemplatingItem?.id === templatingItem.id) {
              setSelectedTemplatingItemId(null);
            }
          },
          onError: () => {
            notify(
              formatMessage(messages.templatingItemDeletedError, {
                typeName: templatingItemTypeDisplay[templatingItem.type],
              }),
              'error',
            );
          },
        },
      );
    },
    [
      templatingItemDeleteMutation,
      notify,
      formatMessage,
      invalidateTemplatingItems,
      selectedTemplatingItem,
      setSelectedTemplatingItemId,
      templatingItemTypeDisplay,
      resetTemplatingItemQueries,
      removeFavoriteTemplate,
    ],
  );

  return {
    deleteTemplatingItem,
  };
};
